import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {Label} from './Label'

const sizeToH = (s: string | number) =>
  [40, 12, 20, 28, 36, 48, 56, 64, 72, 80, 96, 'full'][
    Math.max(Math.min(Number(s), 11), 0)
  ]

type optionShape = {
  /**
   * Item's shown text. Cab be used for search
   */
  label: string
  /**
   * Option value
   */
  value: string | number
}

interface Props {
  /**
   * Additional classes
   */
  className?: string
  /**
   * Input name
   */
  name: string
  /**
   * Input Label
   */
  label?: React.ReactNode
  /**
   * Extra info will be shown next to label
   */
  info?: string
  /**
   * Placeholder
   */
  placeholder?: string
  /**
   * Multiple choice default: false. If it is true it returns as array
   */
  multiple?: boolean
  /**
   * The size attribute causes only ? lines to display at a time; you can scroll to view all the options.
   */
  size?: string | number
  /**
   *  Options array
   */
  options: optionShape[]
  /**
   * show/hide a "Select All" button
   */
  selectAll?: boolean
  /**
   * Element required option
   */
  required?: boolean
  /**
   * Element disable option
   */
  disabled?: boolean

  children?: React.ReactNode

  loading?: boolean
}

export const Select = ({
  name = 'select',
  label,
  info,
  placeholder = 'Select...',
  options = [],
  children,
  loading = false,
  multiple = false,
  size = 0,
  className,
  selectAll = false,
  required = false,
  disabled,
}: Props) => {
  const {
    register,
    formState: {errors},
    setValue,
    // @ts-ignore
    defaultValues,
    // @ts-ignore
    handleFormChange,
  } = useFormContext() || {}

  const ref = useRef<HTMLDivElement>(null)
  const error = errors[name] as any

  // it should return empty array "[]" if it's undefined
  const defaultValue = useMemo(
    () => {
      const value = defaultValues[name]
      return Array.isArray(value)
        ? value.map((i: any) => String(i))
        : [String(value)]
      // return []
    },
    // eslint-disable-next-line
    []
  )

  // it should eliminate "null, NaN, undefined" values
  const optionsStringArray = useMemo(
    () =>
      options.map(item => ({
        label: item.label,
        value: [null, NaN, undefined, ''].includes(item?.value)
          ? ''
          : String(item?.value),
      })),
    [options]
  )

  const [show, setShow] = useState(false)
  const [selected, setSelected] = useState<string[]>([...defaultValue])
  // this is for preventing unnecessary render
  const [isAllSelected, setIsAllSelected] = useState(
    defaultValue.length === optionsStringArray.length
  )

  const isSelected = useCallback(
    (value: any) => selected.indexOf(value) + 1 || false,
    [selected]
  )

  const getLabel = (value: any) =>
    optionsStringArray.find(i => i.value === value)?.label

  const toggleSelect = (e: any) => {
    const value = String(e.target.dataset.value)
    const index = isSelected(value) || false
    const arr = new Set(
      index
        ? selected.splice(index - 1, 1) && selected
        : (multiple ? selected : []).concat([value])
    )
    const values = [...Array.from(arr)]
    setSelected(values)
    setValue(name, multiple ? values : values[0])
    if (!multiple) setShow(false)
    // manual trigger. probably setValues doesnt trig onChange event of form. [GC]
    if (handleFormChange) handleFormChange()
    return !isSelected
  }

  const handleClear = () => {
    setSelected(defaultValue)
    setValue(name, multiple ? defaultValue : defaultValue[0])
    // manual trigger. probably setValues doesnt trig onChange event of form. [GC]
    // if (submitOnChange) handleFormChange();
  }

  const handleSelectAll = () => {
    const allValues = optionsStringArray.map(i => i.value)
    setSelected(allValues)
    setValue(name, multiple ? allValues : allValues[0])
    setIsAllSelected(true)
    // manual trigger. probably setValues doesnt trig onChange event of form. [GC]
    // if (submitOnChange) handleFormChange();
  }

  const handleUnselectAll = () => {
    setSelected([])
    setValue(name, multiple ? [] : '')
    setIsAllSelected(false)
    // manual trigger. probably setValues doesnt trig onChange event of form. [GC]
    // if (submitOnChange) handleFormChange();
  }

  const Options = useMemo(
    () => {
      return (
        <React.Fragment>
          {optionsStringArray.map(i => {
            const active = isSelected(i.value)
            return (
              <li
                key={`${name}-key-${i.value}`}
                className={`hover:bg-gray-200 hover:text-black 
                              cursor-default select-none 
                              relative py-2 pl-3 pr-9'
                              ${active ? 'font-semibold text-black' : ''}
                            `}
                data-value={i.value}>
                {i.label}
                {active && (
                  <i className="icon-ok absolute inset-y-0 right-0 flex items-center pr-4" />
                )}
              </li>
            )
          })}
        </React.Fragment>
      )
    },
    // eslint-disable-next-line
    [optionsStringArray, selected]
  )

  // close when outside click
  useEffect(() => {
    if (size) return
    const handleClickOutside = (e: any) =>
      ref.current && !ref.current.contains(e.target) && setShow(false)

    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
    // eslint-disable-next-line
  }, [ref])

  return (
    <div
      data-cy="Select"
      data-name={name}
      className={Number(size) > 10 ? 'flex flex-col items-stretch h-full' : ''}
      ref={ref}>
      {label && (
        <Label
          className="font-semibold flex-none"
          htmlFor={name}
          label={String(label)}
          info={info}
        />
      )}
      <div className="flex-grow min-w-180">
        <div
          className={
            'relative' +
            (Number(size) > 10 ? ' flex flex-col items-stretch h-full' : '')
          }>
          <button
            type="button"
            onClick={() => setShow(!show)}
            className={`flex-none border rounded p-2 w-full 
             focus:outline-none
             text-form placeholder-form-placeholder
             border-form-border font-form
             ${(!!size || show) && 'rounded-b-none'}
             focus:border-focus-border 
             focus:text-focus 
             focus:placeholder-focus-placeholder
                ${selected.length > 0 && ' border-filled-border text-filled'}
                text-sm
                ${className}`}>
            <span className="block text-left truncate">
              {(selected.length > 1 && `${selected.length} items selected`) ||
                (selected.length === 1 && getLabel(selected[0])) ||
                placeholder}
            </span>
            {(selected.length === 0 || required) && (
              <span className="text-gray-400 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <i className="icon-chevron-down w-5" aria-hidden="true" />
              </span>
            )}
          </button>
          {!required && selected.length > 0 && (
            <button
              type="button"
              className="
              text-gray-400 absolute inset-y-0 right-0 flex items-center pr-2"
              onClick={handleClear}>
              <i className="icon-x-circle w-5" />
            </button>
          )}
          {(!!size || show) && (
            <div
              className={`
                      w-full bg-white
                      border border-form-border
                      rounded  text-base overflow-auto 
                      focus:outline-none sm:text-sm 
                      rounded-t-none
                      ${size ? 'h-full' : 'z-20 shadow'}
                      ${selected.length && 'border-filled-border'}
                      ${
                        loading
                          ? 'bg-gray-300 bg-opacity-75 transition-opacity'
                          : ' ' + (size ? 'relative' : 'absolute')
                      }`}>
              <div className={`h-${sizeToH(size)} flex flex-col flex-stretch`}>
                {children}
                <ul
                  className={` flex-grow overflow-y-auto`}
                  onClick={toggleSelect}>
                  {Options}
                </ul>
                {selectAll && multiple && (
                  <button
                    type="button"
                    onClick={
                      isAllSelected ? handleUnselectAll : handleSelectAll
                    }
                    className={`border-t  p-1 pt-1.5 m-0 w-full rounded-b-md  text-center  focus:outline-none text-xs text-form hover:bg-gray-100 `}>
                    {isAllSelected ? 'Unselect All' : 'Select all'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <input type="hidden" disabled={disabled} {...register(name)} />
      {error && <p className="text-alert pt-1.5 text-xs">{error.message}</p>}
    </div>
  )
}
